const SNAP_MINUTES = 15;
const HOLD_DELAY_MS = 150;
const MOVE_THRESHOLD = 6;
const GRID_HEADER_PX = 28; // 1.75rem top row in the time grid

function formatTime12h(totalMinutes) {
  const hour = Math.floor(totalMinutes / 60);
  const min = totalMinutes % 60;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${String(min).padStart(2, '0')} ${ampm}`;
}

function createPlaceholderEl(height) {
  const el = document.createElement('div');
  Object.assign(el.style, {
    position: 'fixed',
    zIndex: '9999',
    borderRadius: '0.5rem',
    border: '2px dashed rgb(99, 102, 241)',
    backgroundColor: 'rgba(99, 102, 241, 0.08)',
    pointerEvents: 'none',
    transition: 'top 80ms ease-out, left 80ms ease-out',
    height: `${height}px`,
  });
  return el;
}

function createTimeLabelEl() {
  const el = document.createElement('div');
  Object.assign(el.style, {
    position: 'fixed',
    zIndex: '10000',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: 'rgb(99, 102, 241)',
    color: 'white',
    fontSize: '11px',
    fontWeight: '600',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    transition: 'top 80ms ease-out, left 80ms ease-out',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
  });
  return el;
}

export default {
  CalendarEventDrag: {
    mounted() {
      this.drag = null;
      this._suppressClick = false;

      this.onPointerDown = e => this.handlePointerDown(e);
      this.onPointerMove = e => this.handlePointerMove(e);
      this.onPointerUp = e => this.handlePointerUp(e);
      this.onClickCapture = e => {
        if (this._suppressClick) {
          e.preventDefault();
          e.stopPropagation();
          this._suppressClick = false;
        }
      };

      this.el.addEventListener('pointerdown', this.onPointerDown);
      this.el.addEventListener('click', this.onClickCapture, true);
    },

    destroyed() {
      this.el.removeEventListener('pointerdown', this.onPointerDown);
      this.el.removeEventListener('click', this.onClickCapture, true);
      this.teardown();
    },

    handlePointerDown(e) {
      const card = e.target.closest('[data-drag-event-id]');
      if (!card) return;

      e.preventDefault();

      const gridEl = this.el.querySelector('[data-calendar-time-grid]') || this.el;

      const gridRect = gridEl.getBoundingClientRect();
      const cardTop = card.getBoundingClientRect().top;
      const cardY = cardTop - gridRect.top;
      const startMinutes = this.yToMinutes(cardY, gridRect.height);
      const snappedStartMinutes = Math.round(startMinutes / SNAP_MINUTES) * SNAP_MINUTES;

      this.drag = {
        eventId: card.dataset.dragEventId,
        card,
        startX: e.clientX,
        startY: e.clientY,
        offsetY: e.clientY - cardTop,
        cardHeight: card.getBoundingClientRect().height,
        gridEl,
        gridRect,
        startCol: this.getColumnForX(e.clientX),
        currentCol: this.getColumnForX(e.clientX),
        startMinutes: snappedStartMinutes,
        moved: false,
        active: false,
        placeholder: null,
        timeLabel: null,
        minutes: undefined,
        holdTimer: setTimeout(() => this.activate(e.clientX, e.clientY), HOLD_DELAY_MS),
      };

      document.addEventListener('pointermove', this.onPointerMove);
      document.addEventListener('pointerup', this.onPointerUp);
    },

    handlePointerMove(e) {
      if (!this.drag) return;

      if (!this.drag.active) {
        const dist = Math.abs(e.clientX - this.drag.startX) + Math.abs(e.clientY - this.drag.startY);
        if (dist < MOVE_THRESHOLD) return;
        clearTimeout(this.drag.holdTimer);
        this.activate(e.clientX, e.clientY);
      }

      this.drag.moved = true;
      this.drag.currentCol = this.getColumnForX(e.clientX) ?? this.drag.currentCol;
      this.positionPlaceholder(e.clientY);
    },

    handlePointerUp() {
      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
      if (!this.drag) return;

      clearTimeout(this.drag.holdTimer);

      if (this.drag.moved && this.drag.minutes !== undefined) {
        const dayDelta = this.drag.currentCol - this.drag.startCol;
        const timeChanged = this.drag.minutes !== this.drag.startMinutes || dayDelta !== 0;

        if (timeChanged) {
          this._suppressClick = true;
          this.pushEvent('drag_event', {
            id: this.drag.eventId,
            minutes: this.drag.minutes,
            day_delta: dayDelta,
          });
        }
      } else if (!this.drag.moved) {
        const link = this.drag.card.querySelector('a[href]');
        if (link) link.click();
      }

      this.teardown();
    },

    activate(clientX, clientY) {
      if (!this.drag || this.drag.active) return;
      this.drag.active = true;

      this.drag.card.style.opacity = '0.25';
      this.drag.card.style.transition = 'opacity 120ms';

      const placeholder = createPlaceholderEl(this.drag.cardHeight);
      const timeLabel = createTimeLabelEl();
      document.body.append(placeholder, timeLabel);
      this.drag.placeholder = placeholder;
      this.drag.timeLabel = timeLabel;

      this.drag.currentCol = this.getColumnForX(clientX) ?? this.drag.currentCol;
      this.positionPlaceholder(clientY);
    },

    positionPlaceholder(clientY) {
      const { placeholder, timeLabel, gridRect, offsetY } = this.drag;
      if (!placeholder) return;

      const y = clientY - gridRect.top - offsetY;
      const snappedY = this.snapToGrid(y, gridRect.height);
      const minutes = this.yToMinutes(snappedY, gridRect.height);
      this.drag.minutes = minutes;

      const colLeft = this.getColumnLeft(this.drag.currentCol);
      const colWidth = this.getColumnWidth();
      const top = gridRect.top + snappedY;
      const left = colLeft + 4;

      placeholder.style.top = `${top}px`;
      placeholder.style.left = `${left}px`;
      placeholder.style.width = `${colWidth - 8}px`;

      timeLabel.textContent = formatTime12h(minutes);
      timeLabel.style.top = `${top - 22}px`;
      timeLabel.style.left = `${left}px`;
    },

    teardown() {
      if (!this.drag) return;
      clearTimeout(this.drag.holdTimer);
      this.drag.placeholder?.remove();
      this.drag.timeLabel?.remove();
      Object.assign(this.drag.card.style, { opacity: '', transition: '' });
      this.drag = null;
    },

    // --- Column helpers ---

    getColumnWidth() {
      const cols = this.el.querySelectorAll('[data-calendar-day-col]');
      return cols.length ? cols[0].getBoundingClientRect().width : this.el.getBoundingClientRect().width;
    },

    getColumnForX(x) {
      const cols = this.el.querySelectorAll('[data-calendar-day-col]');
      if (!cols.length) return 0;
      for (let i = 0; i < cols.length; i++) {
        const r = cols[i].getBoundingClientRect();
        if (x >= r.left && x <= r.right) return i;
      }
      return this.drag?.startCol ?? 0;
    },

    getColumnLeft(colIndex) {
      const cols = this.el.querySelectorAll('[data-calendar-day-col]');
      if (!cols.length) return this.el.getBoundingClientRect().left;
      return cols[Math.min(colIndex, cols.length - 1)].getBoundingClientRect().left;
    },

    // --- Grid math ---

    snapToGrid(y, gridHeight) {
      const usable = gridHeight - GRID_HEADER_PX;
      const minuteHeight = usable / (24 * 60);
      const rawMinutes = (y - GRID_HEADER_PX) / minuteHeight;
      const snapped = Math.round(rawMinutes / SNAP_MINUTES) * SNAP_MINUTES;
      const clamped = Math.max(0, Math.min(snapped, 24 * 60 - SNAP_MINUTES));
      return GRID_HEADER_PX + clamped * minuteHeight;
    },

    yToMinutes(snappedY, gridHeight) {
      const usable = gridHeight - GRID_HEADER_PX;
      return Math.round((snappedY - GRID_HEADER_PX) / (usable / (24 * 60)));
    },
  },
};
